import {
  type ReactElement,
  type ComponentType,
  isValidElement,
  Children,
} from "react";
import type { NodeTree, Node, CraftConfig } from "../state/types";

let nodeIdCounter = 0;

function generateNodeId(): string {
  return `node-${Date.now()}-${++nodeIdCounter}-${Math.random().toString(36).slice(2, 8)}`;
}

export function parseReactElement(element: ReactElement): NodeTree {
  nodeIdCounter = 0;
  const nodes: Record<string, Node> = {};
  const rootNodeId = parseElement(element, null, nodes);

  return { rootNodeId, nodes };
}

function isElementWrapper(element: ReactElement): boolean {
  // Craft-style Element wrapper: <Element is={Component} ... />
  // We detect by both component name and `is` prop to avoid false positives.
  const type = element.type as {
    name?: string;
    displayName?: string;
    __builderElement?: boolean;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const props = element.props as Record<string, any>;
  const hasIsProp = !!props && "is" in props;
  const typeName = type?.displayName ?? type?.name;
  return (
    hasIsProp && (type?.__builderElement === true || typeName === "Element")
  );
}

function parseElement(
  element: ReactElement,
  parentId: string | null,
  nodes: Record<string, Node>,
): string {
  const nodeId = generateNodeId();
  const wrapper = isElementWrapper(element);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rawProps = { ...(element.props as Record<string, any>) };
  const type = (wrapper ? rawProps.is : element.type) as ComponentType<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Record<string, any>
  >;
  const props = { ...rawProps };
  const explicitCanvas = wrapper
    ? (rawProps.canvas as boolean | undefined)
    : undefined;

  if (wrapper) {
    delete props.is;
    delete props.id;
    delete props.canvas;
  }

  // Extract children from props
  const childElements: ReactElement[] = [];
  if (props.children) {
    Children.forEach(
      props.children as ReactElement | ReactElement[],
      (child) => {
        if (isValidElement(child)) {
          childElements.push(child);
        }
      },
    );
    delete props.children;
  }

  // Read .craft metadata
  const craft = (type as unknown as { craft?: CraftConfig }).craft;

  const childIds: string[] = [];
  const linkedNodes: Record<string, string> = {};
  for (const child of childElements) {
    const childId = parseElement(child, nodeId, nodes);
    if (isElementWrapper(child)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const slotId = (child.props as Record<string, any>).id;
      if (typeof slotId === "string" && slotId.length > 0) {
        linkedNodes[slotId] = childId;
        continue;
      }
    }
    childIds.push(childId);
  }

  const displayName =
    (props.displayName as string | undefined) ??
    craft?.displayName ??
    (typeof type === "string"
      ? type
      : (type.displayName ?? type.name ?? "Unknown"));

  const node: Node = {
    id: nodeId,
    dom: null,
    data: {
      type,
      isCanvas: explicitCanvas ?? craft?.canvas ?? false,
      props: { ...(craft?.props ?? {}), ...props },
      displayName,
      name: displayName,
      custom: craft?.custom ?? {},
      parent: parentId,
      nodes: childIds,
      linkedNodes,
      hidden: false,
    },
    events: { selected: false, hovered: false, dragged: false },
    related: craft?.related ?? {},
    rules: {
      canDrag:
        (craft?.rules?.canDrag as Node["rules"]["canDrag"]) ?? (() => true),
      canDrop:
        (craft?.rules?.canDrop as Node["rules"]["canDrop"]) ?? (() => true),
      canMoveIn:
        (craft?.rules?.canMoveIn as Node["rules"]["canMoveIn"]) ?? (() => true),
      canMoveOut:
        (craft?.rules?.canMoveOut as Node["rules"]["canMoveOut"]) ??
        (() => true),
    },
  };

  nodes[nodeId] = node;
  return nodeId;
}
